import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Website } from '../../types';
import { CheckCircleIcon, ArchiveBoxIcon, TrashIcon, PencilIcon, EyeIcon, TagIcon } from '@heroicons/react/24/outline';
import { IPConfig, getIPName } from '../../utils/ipConfig';

interface WebsiteCardProps {
  website: Website;
  isSelected?: boolean;
  onSelect?: (selected: boolean) => void;
  onDelete?: (id: string) => void;
  onCheckStatus?: (id: string) => void;
  onCheckBackup?: (id: string) => void;
  onDetectCMS?: (id: string) => void;
  isChecking?: boolean;
  isCheckingBackup?: boolean;
  isDetectingCMS?: boolean;
  ipConfig?: IPConfig;
  onNameIP?: (ip: string) => void;
}

const WebsiteCard: React.FC<WebsiteCardProps> = ({ 
  website, 
  isSelected = false, 
  onSelect, 
  onDelete,
  onCheckStatus,
  onCheckBackup,
  onDetectCMS,
  isChecking = false,
  isCheckingBackup = false,
  isDetectingCMS = false,
  ipConfig = {},
  onNameIP
}) => {
  const navigate = useNavigate();
  // État local pour forcer le re-rendu
  const [forceUpdate, setForceUpdate] = useState(1);
  // Référence pour stocker le dernier état de détection
  const wasDetecting = useRef(false);
  // Référence pour le dernier ID de site
  const websiteIdRef = useRef(website.id);
  
  // Effet pour forcer le re-rendu quand isDetectingCMS change de true à false
  useEffect(() => {
    // Si le site a changé, réinitialiser les références
    if (websiteIdRef.current !== website.id) {
      websiteIdRef.current = website.id;
      wasDetecting.current = isDetectingCMS;
      return;
    }
    
    // Quand isDetectingCMS passe de true à false, c'est que la détection est terminée
    if (wasDetecting.current && !isDetectingCMS) {
      // Utiliser un délai plus long pour s'assurer que DOM est mis à jour
      setTimeout(() => {
        // Incrémenter explicitement la valeur précédente
        setForceUpdate(prev => {
          const newValue = prev + 1;
          // Log seulement pour le site concerné
          if (process.env.NODE_ENV === 'development') {
            console.log(`[${website.id}] Fin de détection, rendu forcé`);
          }
          return newValue;
        });
      }, 200);
    }
    
    // Mettre à jour la référence
    wasDetecting.current = isDetectingCMS;
  }, [isDetectingCMS, website.id]);
  
  // Générer une clé unique qui change lorsque les données de détection changent
  const cardKey = `${website.id}-${isDetectingCMS ? 'detecting' : 'idle'}-${forceUpdate}`;
  
  const handleCardClick = () => {
    navigate(`/sites/${website.id}`);
  };
  
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onSelect) {
      onSelect(e.target.checked);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDelete && window.confirm(`Êtes-vous sûr de vouloir supprimer le site "${website.clientName}" ?`)) {
      onDelete(website.id);
    }
  };

  const handleCheckStatus = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onCheckStatus) {
      onCheckStatus(website.id);
    }
  };

  const handleCheckBackup = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onCheckBackup) {
      onCheckBackup(website.id);
    }
  };

  const handleDetectCMS = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDetectCMS) {
      onDetectCMS(website.id);
    }
  };
  
  const handleNameIP = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onNameIP && website.ipAddress) {
      onNameIP(website.ipAddress);
    }
  };
  
  // Obtenir le nom de l'IP s'il existe
  const getIPDisplayName = (ip: string) => {
    if (!ip) return 'Non spécifiée';
    
    const ipName = getIPName(ip);
    if (ipName) {
      return (
        <span 
          className="group flex items-center" 
          title={`IP: ${ip} - ${ipName}`}
        >
          <span className="font-semibold">{ipName}</span>
          <span className="text-xs text-gray-400 dark:text-gray-600 ml-1 group-hover:text-gray-600 dark:group-hover:text-gray-400">
            ({ip})
          </span>
        </span>
      );
    }
    return ip;
  };

  // Déterminer l'état HTTP à afficher et la classe de couleur
  const getHttpStatusDisplay = () => {
    // Vérifier si httpStatus existe et le convertir en minuscules
    const httpStatus = String(website.httpStatus || '').toLowerCase();
    
    if (!website.httpStatus) {
      return {
        text: 'Non vérifié',
        className: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
      };
    }

    if (httpStatus === 'online') {
      return {
        text: 'En ligne',
        className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
      };
    } else if (httpStatus === 'offline') {
      return {
        text: 'Hors ligne',
        className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
      };
    } else if (httpStatus === 'error') {
      return {
        text: 'Erreur',
        className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
      };
    } else {
      return {
        text: website.httpStatus || 'Non vérifié',
        className: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
      };
    }
  };

  // Déterminer le statut du site à afficher et la classe de couleur
  const getSiteStatusDisplay = () => {
    // Assurer que le statut est bien une chaîne de caractères pour les comparaisons
    const status = String(website.status || '').toLowerCase();
    
    if (status === 'active') {
      return {
        text: 'Actif',
        className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
      };
    } else if (status === 'dormant') {
      return {
        text: 'En sommeil',
        className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
      };
    } else if (status === 'terminated') {
      return {
        text: 'Terminé',
        className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
      };
    } else if (status === 'production') {
      return {
        text: 'En production',
        className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
      };
    } else if (status === 'internal') {
      return {
        text: 'Projet Interne',
        className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300'
      };
    } else {
      return {
        text: website.status || 'Non spécifié',
        className: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
      };
    }
  };

  // Fonction pour détecter si le site utilise Minio pour les backups
  const usesMinio = () => {
    return website.backupType === 'minio' && 
           website.bucketName && 
           website.backupStatus && 
           website.backupStatus !== 'not_found';
  };

  // Déterminer le statut de backup à afficher
  const getBackupStatusDisplay = () => {
    // Si le site n'a pas de backup configuré
    if (website.backupType === 'aucun') {
      return {
        text: 'Sans backup',
        className: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
      };
    }
    
    // Pour le type Docker ou Autre, on affiche juste le type
    if (website.backupType === 'docker') {
      return {
        text: 'Backup Docker',
        className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
      };
    }
    
    if (website.backupType === 'autre') {
      return {
        text: 'Backup Autre',
        className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300'
      };
    }
    
    // Pour le type Minio, on utilise le statut existant
    if (!website.backupStatus) {
      return {
        text: 'Backup non vérifié',
        className: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
      };
    }

    switch (website.backupStatus) {
      case 'recent':
        return {
          text: 'Backup récent',
          className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
        };
      case 'warning':
        return {
          text: 'Backup à vérifier',
          className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
        };
      case 'danger':
        return {
          text: 'Backup ancien',
          className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
        };
      case 'not_found':
        return {
          text: 'Pas de backup',
          className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
        };
      default:
        return {
          text: 'Statut inconnu',
          className: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
        };
    }
  };

  // Fonction pour déterminer la classe de couleur en fonction de l'âge du backup
  const getBackupDateColorClass = (dateStr: string | undefined): string => {
    if (!dateStr) return 'text-gray-400'; // Non disponible
    
    const backupDate = new Date(dateStr);
    const now = new Date();
    const diffHours = (now.getTime() - backupDate.getTime()) / (1000 * 60 * 60);
    
    if (diffHours < 24) {
      return 'text-green-600 dark:text-green-400 font-medium';
    } else if (diffHours < 72) {
      return 'text-yellow-600 dark:text-yellow-400 font-medium';
    } else {
      return 'text-red-600 dark:text-red-400 font-medium';
    }
  };

  const httpStatusDisplay = getHttpStatusDisplay();
  const siteStatusDisplay = getSiteStatusDisplay();
  const backupStatusDisplay = getBackupStatusDisplay();

  // Effet pour forcer le rendu lorsque les résultats de détection changent
  useEffect(() => {
    if (website.detectionResults && !isDetectingCMS) {
      // Log seulement en mode développement et pour des changements significatifs
      if (process.env.NODE_ENV === 'development') {
        console.log(`[${website.id}] Résultats de détection mis à jour`);
      }
      setForceUpdate(prev => prev + 1);
    }
  }, [website.detectionResults, isDetectingCMS, website.id]);
  
  // Effet pour forcer le rendu quand les vérifications de backup se terminent
  useEffect(() => {
    if (!isCheckingBackup) {
      // Log seulement en mode développement
      if (process.env.NODE_ENV === 'development') {
        console.log(`[${website.id}] Vérification backup terminée`);
      }
      setForceUpdate(prev => prev + 1);
    }
  }, [isCheckingBackup, website.id, website.backupStatus, website.backupDetails]);
  
  // Calculer les fonctions de détection une seule fois
  const detectionFunctions = useMemo(() => {
    return {
      usesMinio
    };
  }, []);
  
  // Supprimer le cache complexe et utiliser directement les données du site
  // Cela garantit que les icônes se mettent à jour immédiatement

  return (
    <div key={cardKey}>
      <div 
        onClick={handleCardClick}
        className={`block rounded-lg shadow-sm overflow-hidden transition-all duration-150 bg-white dark:bg-gray-800 hover:shadow-md ${isSelected ? 'ring-2 ring-indigo-500 dark:ring-indigo-400' : ''} cursor-pointer`}
        data-website-id={website.id}
      >
        <div className="relative">
          {/* Checkbox pour la sélection multiple */}
          {onSelect && (
            <div className="absolute top-0 left-0 z-10">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={handleCheckboxChange}
                onClick={(e) => e.stopPropagation()}
                className="h-4 w-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:checked:bg-indigo-600"
              />
            </div>
          )}
          
          {/* En-tête avec le nom du client */}
          <div className="bg-primary-500 dark:bg-gray-700 px-4 py-2 text-white flex justify-between items-center">
            <div className="flex items-center space-x-2 truncate">
              <h3 className="text-base font-medium truncate" title={website.clientName}>{website.clientName}</h3>
              
              {/* Indicateur pendant la détection */}
              {isDetectingCMS && (
                <span className="ml-1 flex items-center">
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  <span className="ml-1 text-xs">Détection...</span>
                </span>
              )}
              
              {/* Afficher l'icône MainWP selon la logique demandée */}
              {website.platform === 'WordPress' && (() => {
                const wordpressDetected = website.detectionResults?.wordpress?.detected === true;
                const mainwpDetected = website.detectionResults?.wordpress?.subProbes?.mainwp?.detected === true;
                const mainwpMissing = website.detectionResults?.wordpress?.subProbes?.mainwp?.missingMainWP === true;
                
                // Pendant la détection, garder l'état précédent si les données ne sont pas encore disponibles
                const hasWordPressData = website.detectionResults?.wordpress !== undefined;
                const hasMainWPData = website.detectionResults?.wordpress?.subProbes?.mainwp !== undefined;
                
                // Log de débogage temporaire pour comprendre le problème
                if (website.id === 'mal4u0yq79e') {
                  console.log(`[DEBUG MainWP ${website.id}] wordpress=${wordpressDetected}, mainwp=${mainwpDetected}, missing=${mainwpMissing}, hasWPData=${hasWordPressData}, isDetecting=${isDetectingCMS}`);
                  console.log(`[DEBUG MainWP ${website.id}] detectionResults:`, website.detectionResults);
                }
                
                // Pendant la détection, afficher un spinner
                if (isDetectingCMS) {
                  return (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log(`[MainWP] Détection en cours pour ${website.id}`);
                      }}
                      className="ml-1 hover:opacity-80 transition-opacity relative"
                      title="Détection en cours..."
                      disabled={true}
                    >
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    </button>
                  );
                }
                
                // Logique simplifiée : si on a des données WordPress, on affiche une icône
                if (hasWordPressData) {
                  // Si WordPress détecté ET MainWP détecté ET pas manquant → icône verte
                  if (wordpressDetected && mainwpDetected && !mainwpMissing) {
                    return (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log(`[MainWP] Clic détection pour ${website.id}`);
                          if (onDetectCMS) onDetectCMS(website.id);
                        }}
                        className="ml-1 hover:opacity-80 transition-opacity relative"
                        title="Cliquer pour re-vérifier MainWP"
                        disabled={isDetectingCMS}
                      >
                        <img 
                          src="/icons/mainwp-icon-green.svg" 
                          alt="Géré par MainWP" 
                          className="w-5 h-5"
                          aria-label="Site WordPress géré par MainWP"
                        />
                      </button>
                    );
                  }
                  // Si WordPress détecté mais MainWP non détecté ou manquant → icône rouge
                  else if (wordpressDetected) {
                    return (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log(`[MainWP] Clic détection pour ${website.id}`);
                          if (onDetectCMS) onDetectCMS(website.id);
                        }}
                        className="ml-1 hover:opacity-80 transition-opacity relative"
                        title="Cliquer pour re-vérifier MainWP"
                        disabled={isDetectingCMS}
                      >
                        <img 
                          src="/icons/mainwp-icon-red.svg" 
                          alt="WordPress non géré par MainWP" 
                          className="w-5 h-5"
                          aria-label="Site WordPress non géré par MainWP"
                        />
                      </button>
                    );
                  }
                }
                
                // Si pas de données WordPress ou WordPress non détecté, ne rien afficher
                if (website.id === 'mal4u0yq79e') {
                  console.log(`[DEBUG MainWP ${website.id}] Pas d'icône affichée`);
                }
                return null;
              })()}
              
              {/* Afficher l'icône Turnstile selon la logique demandée */}
              {website.captchaType === 'Turnstile' && (() => {
                const turnstileDetected = website.detectionResults?.wordpress?.subProbes?.turnstile?.detected === true;
                const isConfigured = website.detectionResults?.wordpress?.subProbes?.turnstile?.isConfigured === true;
                
                // Vérifier si on a des données MainWP avec plugins
                const hasMainWPPluginData = website.detectionResults?.wordpress?.subProbes?.mainwp?.plugins && 
                                          Array.isArray(website.detectionResults.wordpress.subProbes.mainwp.plugins);
                
                // Chercher le plugin Turnstile dans les plugins MainWP
                let turnstilePlugin = null;
                let turnstileActive = false;
                
                if (hasMainWPPluginData) {
                  turnstilePlugin = website.detectionResults!.wordpress.subProbes.mainwp.plugins.find((plugin: any) => {
                    const pluginSlug = plugin.slug || plugin.name || '';
                    const pluginName = pluginSlug.split('/')[0];
                    return pluginName === 'simple-cloudflare-turnstile' || 
                           pluginName.includes('turnstile') || 
                           pluginName.includes('cloudflare');
                  });
                  
                  if (turnstilePlugin) {
                    turnstileActive = turnstilePlugin.active || turnstilePlugin.status === 'active';
                  }
                }
                
                // Vérifier si on a des données de détection Turnstile directes
                const hasTurnstileData = website.detectionResults?.wordpress?.subProbes?.turnstile !== undefined;
                const hasWordPressData = website.detectionResults?.wordpress !== undefined;
                
                // Pendant la détection, afficher un spinner
                if (isDetectingCMS) {
                  return (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log(`[Turnstile] Détection en cours pour ${website.id}`);
                      }}
                      className="ml-1 hover:opacity-80 transition-opacity relative"
                      title="Détection en cours..."
                      disabled={true}
                    >
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    </button>
                  );
                }
                
                // Logique simplifiée : si on a des données WordPress, on peut déterminer l'état de Turnstile
                if (hasWordPressData) {
                  // Priorité aux données MainWP si disponibles (plus fiables)
                  if (hasMainWPPluginData) {
                    // Si le plugin est trouvé ET actif dans MainWP → icône verte
                    if (turnstilePlugin && turnstileActive) {
                      return (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log(`[Turnstile] Clic détection pour ${website.id}`);
                            if (onDetectCMS) onDetectCMS(website.id);
                          }}
                          className="ml-1 hover:opacity-80 transition-opacity relative"
                          title="Cliquer pour re-vérifier Turnstile"
                          disabled={isDetectingCMS}
                        >
                          <img 
                            src="/icons/turnstile-icon-green.svg" 
                            alt="Turnstile actif" 
                            className="w-5 h-5"
                            aria-label="Protection Turnstile active et configurée"
                          />
                        </button>
                      );
                    }
                    // Si captchaType=Turnstile mais plugin absent ou inactif dans MainWP → icône rouge
                    else {
                      return (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log(`[Turnstile] Clic détection pour ${website.id}`);
                            if (onDetectCMS) onDetectCMS(website.id);
                          }}
                          className="ml-1 hover:opacity-80 transition-opacity relative"
                          title="Cliquer pour re-vérifier Turnstile"
                          disabled={isDetectingCMS}
                        >
                          <img 
                            src="/icons/turnstile-icon-red.svg" 
                            alt="Turnstile inactif" 
                            className="w-5 h-5"
                            aria-label="Protection Turnstile désactivée ou non configurée"
                          />
                        </button>
                      );
                    }
                  }
                  // Sinon, utiliser les données de détection directe Turnstile si disponibles
                  else if (hasTurnstileData) {
                    // Si le plugin est détecté (avec ou sans configuration) → icône verte
                    // Car si on détecte Turnstile via HTML/JS, c'est qu'il fonctionne
                    if (turnstileDetected) {
                      return (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log(`[Turnstile] Clic détection pour ${website.id}`);
                            if (onDetectCMS) onDetectCMS(website.id);
                          }}
                          className="ml-1 hover:opacity-80 transition-opacity relative"
                          title="Cliquer pour re-vérifier Turnstile"
                          disabled={isDetectingCMS}
                        >
                          <img 
                            src="/icons/turnstile-icon-green.svg" 
                            alt="Turnstile actif" 
                            className="w-5 h-5"
                            aria-label="Protection Turnstile active et configurée"
                          />
                        </button>
                      );
                    }
                    // Si captchaType=Turnstile mais plugin non détecté → icône rouge
                    else {
                      return (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log(`[Turnstile] Clic détection pour ${website.id}`);
                            if (onDetectCMS) onDetectCMS(website.id);
                          }}
                          className="ml-1 hover:opacity-80 transition-opacity relative"
                          title="Cliquer pour re-vérifier Turnstile"
                          disabled={isDetectingCMS}
                        >
                          <img 
                            src="/icons/turnstile-icon-red.svg" 
                            alt="Turnstile inactif" 
                            className="w-5 h-5"
                            aria-label="Protection Turnstile désactivée ou non configurée"
                          />
                        </button>
                      );
                    }
                  }
                  // Si on a des données WordPress mais pas de données Turnstile spécifiques, 
                  // on assume que Turnstile n'est pas configuré → icône rouge
                  else {
                    return (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log(`[Turnstile] Clic détection pour ${website.id}`);
                          if (onDetectCMS) onDetectCMS(website.id);
                        }}
                        className="ml-1 hover:opacity-80 transition-opacity relative"
                        title="Cliquer pour re-vérifier Turnstile"
                        disabled={isDetectingCMS}
                      >
                        <img 
                          src="/icons/turnstile-icon-red.svg" 
                          alt="Turnstile inactif" 
                          className="w-5 h-5"
                          aria-label="Protection Turnstile désactivée ou non configurée"
                        />
                      </button>
                    );
                  }
                }
                
                // Si aucune donnée WordPress disponible, ne pas afficher d'icône
                return null;
              })()}
              
              {/* Afficher l'icône Cloudflare selon la logique demandée */}
              {(() => {
                const cloudflareDetected = website.detectionResults?.cloudflare?.detected === true;
                const hasCloudflareData = website.detectionResults?.cloudflare !== undefined;
                
                // Pendant la détection, afficher un spinner
                if (isDetectingCMS) {
                  return (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log(`[Cloudflare] Détection en cours pour ${website.id}`);
                      }}
                      className="ml-1 hover:opacity-80 transition-opacity relative"
                      title="Détection en cours..."
                      disabled={true}
                    >
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    </button>
                  );
                }
                
                // Si on a des données de détection Cloudflare
                if (hasCloudflareData) {
                  // Si Cloudflare est détecté → icône orange Cloudflare
                  if (cloudflareDetected) {
                    return (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log(`[Cloudflare] Clic détection pour ${website.id}`);
                          if (onDetectCMS) onDetectCMS(website.id);
                        }}
                        className="ml-1 hover:opacity-80 transition-opacity relative"
                        title="Site protégé par Cloudflare - Cliquer pour re-vérifier"
                        disabled={isDetectingCMS}
                      >
                        <img 
                          src="/icons/cloudflare.svg" 
                          alt="Protégé par Cloudflare" 
                          className="w-5 h-5"
                          aria-label="Site protégé par Cloudflare"
                        />
                      </button>
                    );
                  }
                }
                
                // Si pas de données Cloudflare ou Cloudflare non détecté, ne pas afficher d'icône
                return null;
              })()}
              
              {/* Afficher l'icône Cookies selon la logique demandée */}
              {(() => {
                const cookiesData = website.detectionResults?.wordpress?.subProbes?.cookies;
                const hasCookiesData = cookiesData !== undefined;
                const cookiesDetected = cookiesData?.detected === true;
                const detectedPlugin = cookiesData?.detectedPlugin;
                const configuredPlugin = website.cookiesManagement;
                const cookiesStatus = cookiesData?.status;
                
                // Pendant la détection, afficher un spinner
                if (isDetectingCMS) {
                  return (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log(`[Cookies] Détection en cours pour ${website.id}`);
                      }}
                      className="ml-1 hover:opacity-80 transition-opacity relative"
                      title="Détection en cours..."
                      disabled={true}
                    >
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    </button>
                  );
                }
                
                // Si on a des données de détection cookies
                if (hasCookiesData && cookiesStatus) {
                  let iconSrc = '';
                  let altText = '';
                  let titleText = '';
                  
                  switch (cookiesStatus) {
                    case 'match':
                      // Vert : même plugin que celui configuré
                      iconSrc = '/icons/cookies-icon-green.svg';
                      altText = 'Plugin cookies conforme';
                      titleText = `Plugin cookies conforme : ${detectedPlugin?.name || 'Plugin détecté'} correspond à la configuration`;
                      break;
                    case 'mismatch':
                      // Orange : plugin différent de celui configuré
                      iconSrc = '/icons/cookies-icon-orange.svg';
                      altText = 'Plugin cookies différent';
                      titleText = `Plugin cookies différent : ${detectedPlugin?.name || 'Plugin détecté'} ne correspond pas à la configuration (${configuredPlugin})`;
                      break;
                    case 'missing':
                      // Rouge : aucun plugin détecté alors qu'un gestionnaire est configuré
                      iconSrc = '/icons/cookies-icon-red.svg';
                      altText = 'Plugin cookies manquant';
                      titleText = `Plugin cookies manquant : aucun plugin détecté mais ${configuredPlugin} est configuré`;
                      break;
                    case 'unspecified_detected':
                      // Gris : plugin détecté mais utilisateur n'a pas spécifié de gestionnaire
                      iconSrc = '/icons/cookies-icon-gray.svg';
                      altText = 'Plugin cookies non spécifié';
                      titleText = `Plugin cookies détecté : ${detectedPlugin?.name || 'Plugin détecté'} mais aucun gestionnaire spécifié dans la configuration`;
                      break;
                    default:
                      return null;
                  }
                  
                  return (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log(`[Cookies] Clic détection pour ${website.id}`);
                        if (onDetectCMS) onDetectCMS(website.id);
                      }}
                      className="ml-1 hover:opacity-80 transition-opacity relative"
                      title={`${titleText} - Cliquer pour re-vérifier`}
                      disabled={isDetectingCMS}
                    >
                      <img 
                        src={iconSrc}
                        alt={altText}
                        className="w-5 h-5"
                        aria-label={titleText}
                      />
                    </button>
                  );
                }
                
                // Si pas de données cookies, ne pas afficher d'icône
                return null;
              })()}
              
              {/* Afficher l'icône SMTP selon la logique demandée */}
              {(() => {
                const smtpData = website.detectionResults?.wordpress?.subProbes?.smtp;
                const hasSMTPData = smtpData !== undefined;
                const smtpDetected = smtpData?.detected === true;
                const targetPluginFound = smtpData?.targetPluginFound === true;
                const otherSMTPFound = smtpData?.otherSMTPFound === true;
                const detectedPlugin = smtpData?.detectedPlugin;
                const configuredEmailType = website.emailType;
                const smtpStatus = smtpData?.status;
                
                // Si "Sans email" configuré, ne rien afficher
                if (configuredEmailType === 'aucun') {
                  return null;
                }
                
                // Pendant la détection, afficher un spinner
                if (isDetectingCMS) {
                  return (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log(`[SMTP] Détection en cours pour ${website.id}`);
                      }}
                      className="ml-1 hover:opacity-80 transition-opacity relative"
                      title="Détection en cours..."
                      disabled={true}
                    >
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    </button>
                  );
                }
                
                // Si on a des données de détection SMTP
                if (hasSMTPData && smtpStatus) {
                  let iconSrc = '';
                  let altText = '';
                  let titleText = '';
                  
                  switch (smtpStatus) {
                    case 'match':
                      // 🟢 Vert : Plugin SMTP Mailer détecté et actif
                      iconSrc = '/icons/smtp-icon-green.svg';
                      altText = 'SMTP Mailer actif';
                      titleText = `SMTP Mailer par naa986 détecté et actif`;
                      break;
                    case 'other_plugin':
                      // 🟠 Orange : Plugin détecté mais c'est un autre que SMTP Mailer naa986
                      iconSrc = '/icons/smtp-icon-orange.svg';
                      altText = 'Autre plugin SMTP';
                      titleText = `Autre plugin SMTP détecté : ${detectedPlugin?.name || 'Plugin SMTP'} (pas SMTP Mailer par naa986)`;
                      break;
                    case 'missing':
                      // 🔴 Rouge : Plugin non détecté ou désactivé mais Type d'email choisi dans la fiche
                      iconSrc = '/icons/smtp-icon-red.svg';
                      altText = 'Plugin SMTP manquant';
                      titleText = `Plugin SMTP manquant ou inactif alors que le type d'email est configuré (${configuredEmailType})`;
                      break;
                    case 'hidden':
                      // Ne rien afficher si "Sans email"
                      return null;
                    default:
                      return null;
                  }
                  
                  return (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log(`[SMTP] Clic détection pour ${website.id}`);
                        if (onDetectCMS) onDetectCMS(website.id);
                      }}
                      className="ml-1 hover:opacity-80 transition-opacity relative"
                      title={`${titleText} - Cliquer pour re-vérifier`}
                      disabled={isDetectingCMS}
                    >
                      <img 
                        src={iconSrc}
                        alt={altText}
                        className="w-5 h-5"
                        aria-label={titleText}
                      />
                    </button>
                  );
                }
                
                // Si pas de données SMTP, ne pas afficher d'icône
                return null;
              })()}
              
              {/* Afficher l'icône MinIO selon la logique de détection */}
              {(() => {
                const minioData = website.detectionResults?.minio;
                const hasMinIOData = minioData !== undefined;
                const minioDetected = minioData?.detected === true;
                const bucketExists = minioData?.bucketExists === true;
                const connectionValid = minioData?.connectionValid === true;
                

                
                // Pendant la détection, afficher un spinner
                if (isDetectingCMS) {
                  return (
                    <button
                      className="ml-1 transition-opacity relative"
                      disabled={true}
                    >
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    </button>
                  );
                }
                
                // Si pas de données MinIO, ne pas afficher d'icône
                if (!hasMinIOData || !minioDetected) {
                  return null;
                }
                
                // Déterminer l'état et l'apparence de l'icône
                let iconClass = 'w-5 h-5';
                let titleText = '';
                let buttonClass = 'ml-1 hover:opacity-80 transition-opacity relative';
                
                if (connectionValid && bucketExists) {
                  // Vert : Configuration MinIO valide et bucket accessible
                  iconClass += ' filter brightness-0 saturate-100 hue-rotate-90 brightness-150';
                  titleText = `MinIO configuré et fonctionnel (bucket: ${minioData.bucketName || 'configuré'})`;
                } else if (connectionValid && !bucketExists) {
                  // Orange : Connexion OK mais bucket manquant
                  iconClass += ' filter brightness-0 saturate-100 hue-rotate-30';
                  titleText = `MinIO connecté mais bucket manquant ou inaccessible (${minioData.bucketName || 'non spécifié'})`;
                } else {
                  // Rouge : Problème de connexion MinIO
                  iconClass += ' filter brightness-0 saturate-100 hue-rotate-0 brightness-75';
                  titleText = `MinIO configuré mais connexion échouée`;
                }
                
                return (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (onCheckBackup) onCheckBackup(website.id);
                    }}
                    className={buttonClass}
                    title={titleText}
                    disabled={isCheckingBackup}
                  >
                    {isCheckingBackup ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <img 
                        src="/icons/minio-icon.svg" 
                        alt="MinIO" 
                        className={iconClass}
                        aria-label={titleText}
                      />
                    )}
                  </button>
                );
              })()}
            </div>
            
            {/* Actions rapides */}
            <div className="flex items-center space-x-1">
              {onDelete && (
                <button
                  onClick={handleDelete}
                  title="Supprimer"
                  className="p-1 text-white hover:bg-red-500 rounded transition-colors"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/sites/${website.id}/edit`);
                }}
                title="Éditer"
                className="p-1 text-white hover:bg-indigo-500 rounded transition-colors"
              >
                <PencilIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
          
          {/* Contenu de la carte */}
          <div className="p-4">
            {/* URL du site */}
            <div className="mb-3">
              <a 
                href={website.url.startsWith('http') ? website.url : `https://${website.url}`} 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-blue-600 dark:text-blue-400 hover:underline font-medium flex items-center"
              >
                <EyeIcon className="h-4 w-4 mr-1" />
                <span className="truncate" title={website.url}>
                  {website.url.replace(/^https?:\/\//i, '')}
                </span>
              </a>
            </div>

            {/* Tags */}
            {website.tags && website.tags.length > 0 && (
              <div className="mb-4">
                <div className="flex flex-wrap gap-1">
                  {website.tags.slice(0, 3).map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                      title={tag}
                    >
                      {tag}
                    </span>
                  ))}
                  {website.tags.length > 3 && (
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-400"
                      title={`+${website.tags.length - 3} autres tags: ${website.tags.slice(3).join(', ')}`}
                    >
                      +{website.tags.length - 3}
                    </span>
                  )}
                </div>
              </div>
            )}
            
            {/* Informations principales */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              {/* Statut HTTP */}
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 dark:text-gray-400">Disponibilité HTTP</span>
                <div className="flex items-center mt-1">
                  {isChecking ? (
                    <span className="animate-pulse text-xs rounded px-2 py-0.5 bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                      Vérification...
                    </span>
                  ) : (
                    <div className="flex items-center">
                      <button 
                        onClick={handleCheckStatus}
                        className={`text-xs rounded px-2 py-0.5 ${getHttpStatusDisplay().className} hover:opacity-80 transition-opacity`}
                        title="Cliquer pour vérifier à nouveau"
                      >
                        {getHttpStatusDisplay().text}
                      </button>
                      
                      {/* Badge htaccess */}
                      {website.statusDetails?.htaccessAuthUsed && (
                        <span 
                          className="ml-1 px-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 rounded-sm"
                          title="Authentification htaccess utilisée"
                        >
                          htaccess
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Type de plateforme */}
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 dark:text-gray-400">Plateforme</span>
                <span className="mt-1 text-sm truncate" title={website.platform}>
                  {website.platform || 'Non spécifiée'}
                </span>
              </div>
            </div>
            
            {/* Deuxième ligne d'informations */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              {/* Statut d'activité */}
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 dark:text-gray-400">État du contrat</span>
                <span className={`text-xs rounded px-2 py-0.5 mt-1 inline-block ${getSiteStatusDisplay().className}`}>
                  {getSiteStatusDisplay().text}
                </span>
              </div>
              
              {/* Statut de backup */}
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 dark:text-gray-400">Backup</span>
                <div className="flex items-center mt-1">
                  {isCheckingBackup ? (
                    <span className="animate-pulse text-xs rounded px-2 py-0.5 bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                      Vérification...
                    </span>
                  ) : (
                    <button 
                      onClick={handleCheckBackup}
                      className={`text-xs rounded px-2 py-0.5 ${getBackupStatusDisplay().className} hover:opacity-80 transition-opacity`}
                      title="Cliquer pour vérifier à nouveau"
                    >
                      {getBackupStatusDisplay().text}
                    </button>
                  )}
                </div>
              </div>
            </div>
            
            {/* IP et serveur */}
            <div className="grid grid-cols-1 gap-2">
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 dark:text-gray-400">Serveur</span>
                <button 
                  onClick={handleNameIP}
                  disabled={!onNameIP || !website.ipAddress}
                  className={`mt-1 text-sm truncate text-left ${(onNameIP && website.ipAddress) ? 'hover:text-indigo-600 dark:hover:text-indigo-400 cursor-pointer' : ''}`}
                  title={website.ipAddress ? `Cliquer pour nommer l'IP: ${website.ipAddress}` : 'Aucune IP spécifiée'}
                >
                  {website.ipAddress ? getIPDisplayName(website.ipAddress) : 'Non spécifiée'}
                </button>
              </div>
            </div>

            {/* Tags */}
            {website.tags && website.tags.length > 0 && (
              <div className="mt-3">
                <span className="text-xs text-gray-500 dark:text-gray-400">Tags</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {website.tags.slice(0, 3).map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                    >
                      {tag}
                    </span>
                  ))}
                  {website.tags.length > 3 && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                      +{website.tags.length - 3}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebsiteCard;